import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { LIBRARY_LINKS } from '../../constants';
import { Button, IconDownload as DownloadIcon, IconInfoCircle as InfoIcon } from '../../core';
import { useDownloadFile } from '../../hooks/useDownloadFile';
import { ReactComponent as ExcelIcon } from '../../images/icons/excel.svg';
import { ReactComponent as FigmaIcon } from '../../images/icons/figma.svg';
import { ReactComponent as PowerIcon } from '../../images/icons/power-bi.svg';
import Flex from '../Flex';
import { Select } from '../Select';

type DownloadSelectProps = {
  className?: string;
  onOpenChange?: (isOpen: boolean) => void;
};

const DownloadSelect = ({ className, onOpenChange }: DownloadSelectProps) => {
  const downloadFile = useDownloadFile();
  const selectRef = useRef<HTMLDivElement>(null);

  const handleInfoClick = (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    window.location.href = path;
  };

  useEffect(() => {
    if (!onOpenChange || !selectRef.current) return undefined;

    const observer = new MutationObserver(() => {
      const trigger = selectRef.current?.querySelector('[role="combobox"]');
      const isOpen = trigger?.getAttribute('aria-expanded') === 'true';
      onOpenChange(isOpen);
    });

    observer.observe(selectRef.current, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-expanded'],
    });

    return () => observer.disconnect();
  }, [onOpenChange]);

  const selectOptions = [
    {
      value: 'powerBI',
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 0px' }}
          align="center"
          justify="space-between"
          onPointerDown={() => downloadFile('PBI Library.pbix', LIBRARY_LINKS.pbi_lib_path)}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PowerIcon />
            <span style={{ marginLeft: '8px' }}>Power BI Library</span>
          </div>
          <Flex gap="8px" align="center">
            <Button
              onPointerDown={(e) => handleInfoClick(e, '/dev-resources/powerbikit')}
              iconOnly
              variant="ghost"
              size="XS"
              icon={<InfoIcon />}
              aria-label="About the Power BI Library"
            />
            <Button
              iconOnly
              size="XS"
              icon={<DownloadIcon />}
              aria-label="Download the Power BI Library"
            />
          </Flex>
        </Flex>
      ),
    },
    {
      value: 'excel',
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 0px' }}
          align="center"
          justify="space-between"
          onPointerDown={() => downloadFile('Excel Library.xlsx', LIBRARY_LINKS.excel_lib_path)}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ExcelIcon />
            <span style={{ marginLeft: '8px' }}>Excel Library</span>
          </div>
          <Flex gap="8px" align="center">
            <Button
              onPointerDown={(e) => handleInfoClick(e, '/dev-resources/excel-library')}
              iconOnly
              variant="ghost"
              size="XS"
              icon={<InfoIcon />}
              aria-label="About the Excel Library"
            />
            <Button
              iconOnly
              size="XS"
              icon={<DownloadIcon />}
              aria-label="Download the Excel Library"
            />
          </Flex>
        </Flex>
      ),
    },
    {
      value: 'figma',
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 0px' }}
          align="center"
          justify="space-between"
          onPointerDown={() => downloadFile('Figma Library.fig', LIBRARY_LINKS.figma_lib_path)}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FigmaIcon />
            <span style={{ marginLeft: '8px' }}>Figma Library</span>
          </div>
          <Flex gap="8px" align="center">
            <Button
              onPointerDown={(e) => handleInfoClick(e, '/dev-resources/figma-library')}
              iconOnly
              variant="ghost"
              size="XS"
              icon={<InfoIcon />}
              aria-label="About the Figma Library"
            />
            <Button
              iconOnly
              size="XS"
              icon={<DownloadIcon />}
              aria-label="Download the Figma Library"
            />
          </Flex>
        </Flex>
      ),
    },
  ];

  return (
    <div ref={selectRef}>
      <Select
        paddingZero
        triggerLabel={<span>Download</span>}
        className={className}
        data={selectOptions}
      />
    </div>
  );
};

const StyledDownloadSelect = styled(DownloadSelect)`
  position: relative !important;
  z-index: 100 !important;

  button[role='combobox'] {
    min-width: auto;
    width: fit-content;
    padding: 8px 12px;
    background-color: ${(props) => props.theme.primaryAction};
    color: ${(props) => props.theme.primaryTextStrong} !important;
    border-radius: 4px;
    font-size: ${(props) => props.theme.fontSizes.sm};
    line-height: ${(props) => props.theme.lineHeights.sm};
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
    display: flex;
    align-items: center;
    gap: 12px;

    &:hover {
      background-color: ${(props) => props.theme.primaryActionHover};
      color: ${(props) => props.theme.primaryTextStrong} !important;
    }

    > .select-selected-option {
      color: ${(props) => props.theme.primaryTextStrong} !important;
    }

    .select-selected-option__label {
      color: ${(props) => props.theme.primaryTextStrong} !important;
      padding-right: 12px;
      border-right: 1px solid rgba(255, 255, 255, 0.3); /* tokens-allow: pending design decision */

      span {
        color: ${(props) => props.theme.primaryTextStrong} !important;
      }
    }

    .select-icon {
      margin-left: 0;
      padding-left: 12px;

      &::before {
        background-color: rgba(255, 255, 255, 0.3); /* tokens-allow: pending design decision */
        left: 0;
      }
    }

    svg {
      path {
        fill: ${(props) => props.theme.primaryTextStrong};
      }
    }
  }

  .select-selected-option__icon {
    margin-right: 0;
    display: flex;
    align-items: center;
  }

  [role='option'] {
    .select-options-item__icon {
      margin-right: 8px;
      display: flex;
      align-items: center;
    }

    .select-options-item__label {
      display: flex;
      justify-content: space-between;
      width: 100%;
      padding: 0px;
    }

    &[data-state='checked'] {
      background-color: transparent !important;
      font-weight: 400 !important;
    }
  }
`;

export default StyledDownloadSelect;
